import React, { Dispatch, FC, SetStateAction, useState } from 'react'

import { LoaderCircle } from 'lucide-react'

import { Button } from '../../../../../ui/button'
import { ExhibitorAnswerInput } from '../../../../../../pages/organizer/exhibitorProfile/components/ExhibitorAnswerInput'
import { IconCreate } from '../../../../../icons/IconCreate'
import customImageComponent from '../../../../../common/CustomImageComponent'

import FirestoreService from '../../../../../../services/firestoreService'

import useToasterHelper from '../../../../../../helpers/ToasterHelper'

import {
  IAnswer,
  TEventRegisteredUsers,
} from '../../../../../../models/event-registered-users/event-registered-users.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IQuestion } from '../../../../../../models/events/event.interface'

import { MESSAGES_CONST } from '../../../../../../const/messages-const'
import { CONST } from '../../../../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

interface AnswerProps {
  riders: IRegistrationByDayInterface[]
  comment?: IAnswer
  question: IQuestion
  answersValues: IAnswer[]
  setAnswersValues: Dispatch<SetStateAction<IAnswer[]>>
  eventRegisteredUser: TEventRegisteredUsers | null
  setEventRegisteredUser: (value: TEventRegisteredUsers | null) => void
}

const Answer: FC<AnswerProps> = ({
  riders,
  comment,
  question,
  answersValues,
  setAnswersValues,
  eventRegisteredUser,
  setEventRegisteredUser,
}) => {
  const toastFunctions = useToasterHelper()

  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const onSave = async () => {
    try {
      setLoading(true)
      await FirestoreService.updateItem(
        COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
        eventRegisteredUser?.id,
        { answers: answersValues }
      )
      if (eventRegisteredUser) {
        setEventRegisteredUser({
          ...eventRegisteredUser,
          answers: answersValues,
        })
      }
      toastFunctions.success({ message: 'Updated successfully.' })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      console.error('error:', error)
    } finally {
      setLoading(false)
      setIsEdit(false)
    }
  }

  const onCancel = async () => {
    setIsEdit(false)
    setAnswersValues(eventRegisteredUser?.answers || [])
  }

  const onEdit = async () => {
    setIsEdit(true)
  }

  return (
    <div className="border p-5 border-black/10 rounded-xl">
      <ExhibitorAnswerInput
        isCollapsible={true}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        key={comment?.id}
        comment={comment}
        question={question}
        isEditModeAnswers={isEdit}
        setAnswersValues={setAnswersValues}
        actions={
          isEdit ? (
            <div className="ml-auto flex items-center gap-2">
              <Button
                disabled={loading}
                variant="secondary"
                className="rounded-full text-sm py-[5px] px-2 min-w-[80px] font-normal"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                disabled={loading}
                variant="red"
                className="rounded-full text-sm py-1 px-2 min-w-[80px] font-normal"
                onClick={onSave}
              >
                {loading && <LoaderCircle className="animate-spin" />} Save
              </Button>
            </div>
          ) : (
            <Button
              disabled={loading}
              variant="ghost"
              className="group ml-auto rounded-full p-0 bg-white w-auto h-auto"
              size="icon"
              onClick={onEdit}
            >
              <IconCreate className="!w-5 !h-auto group-hover:fill-SeabiscuitMainThemeColor" />
            </Button>
          )
        }
      />
      {isOpen && riders?.[0] && (
        <div className="mt-5 pt-4 border-t border-t-black/10">
          <h5 className="text-base font-medium text-black">Team</h5>
          <div className="space-y-4 mt-3">
            {riders.map((rider, index) => {
              const horses = rider.horsesSelected?.map(({ name }) => name).join(', ')
              return (
                <div key={index} className="flex items-center gap-3">
                  <div>
                    {customImageComponent(
                      rider?.riderProfilePicture,
                      rider.riderName,
                      'w-[45px] h-[45px] rounded-full border relative z-10'
                    )}
                    {customImageComponent(
                      rider?.horseProfilePicture,
                      rider.horseName,
                      'w-[45px] h-[45px] rounded-full border -ml-3'
                    )}
                  </div>
                  <div>
                    <p className="text-black/50 text-base">Rider: {rider.riderName}</p>
                    <p className="text-black/50 text-base">Horse: {horses}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

interface QATabProps {
  eventReviewPublish: IEventReviewPublish | null
  eventRegisteredUser: TEventRegisteredUsers | null
  setEventRegisteredUser: (value: TEventRegisteredUsers | null) => void
  riders: IRegistrationByDayInterface[]
}

export const QATab: FC<QATabProps> = ({
  riders,
  eventReviewPublish,
  eventRegisteredUser,
  setEventRegisteredUser,
}) => {
  const questions = eventReviewPublish?.EventQuestions || []
  const [answersValues, setAnswersValues] = useState<IAnswer[]>(eventRegisteredUser?.answers || [])

  return (
    <div className="flex flex-col gap-6">
      {questions?.[0] ? (
        questions?.map((question) => {
          const comments =
            answersValues?.filter((answer) => answer.questionId === question.id) || []
          return (
            question.value &&
            (comments?.[0] ? (
              comments.map((comment) => (
                <Answer
                  riders={riders}
                  key={comment.id}
                  comment={comment}
                  question={question}
                  answersValues={answersValues}
                  setAnswersValues={setAnswersValues}
                  eventRegisteredUser={eventRegisteredUser}
                  setEventRegisteredUser={setEventRegisteredUser}
                />
              ))
            ) : (
              <Answer
                riders={riders}
                question={question}
                answersValues={answersValues}
                setAnswersValues={setAnswersValues}
                eventRegisteredUser={eventRegisteredUser}
                setEventRegisteredUser={setEventRegisteredUser}
              />
            ))
          )
        })
      ) : (
        <p className="text-black/50 text-center">No questions</p>
      )}
    </div>
  )
}
